import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HeaderStatusBar, BottomNavBar } from '@components/layout';
import { useAuthStore } from '@app/stores';
import type { TabName, UserProfile } from '@types';
import { BattleContent } from './contents/BattleContent';
import { QuestsContent } from './contents/QuestsContent';
import { ShopContent } from './contents/ShopContent';
import { FrensContent } from './contents/FrensContent';
import { RankingContent } from './contents/RankingContent';
import bgHome from '@/assets/home/bg-home.webp';

export const MainGamePage = () => {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<TabName>('battle');
  const [direction, setDirection] = useState(0);
  const isFirstMount = useRef(true);
  const [battleContentKey, setBattleContentKey] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const tabOrder: TabName[] = ['quests', 'shop', 'battle', 'frens', 'ranking'];

  const handleTabChange = (newTab: TabName) => {
    const currentIndex = tabOrder.indexOf(activeTab);
    const newIndex = tabOrder.indexOf(newTab);
    setDirection(newIndex > currentIndex ? 1 : -1);
    setActiveTab(newTab);
    isFirstMount.current = false;
  };

  const handleBattleContentRefresh = () => {
    setBattleContentKey((prev) => prev + 1);
  };

  // User profile from auth store - map to UserProfile interface
  const userProfile: UserProfile = user
    ? {
        id: user.id,
        address: user.address,
        username: user.username,
        gold_balance: user.gold || 0,
        sei_token_balance: user.sei || 0,
        current_energy: user.energy || 0,
        max_energy: 20,
        scores: user.scores || 0,
        referral_code: '',
        level: user.level,
        experience: user.experience,
        pvp_unlocked: true,
        totalCheckIn: user.totalCheckIn || 0,
      }
    : {
        id: '',
        address: '',
        gold_balance: 0,
        sei_token_balance: 0,
        current_energy: 0,
        max_energy: 20,
        scores: 0,
        referral_code: '',
        level: 1,
        experience: 0,
        pvp_unlocked: false,
        totalCheckIn: 0,
      };

  const contentVariants = {
    enter: (direction: number) => {
      if (isFirstMount.current) {
        return { x: 0, opacity: 0 };
      }
      return {
        x: direction > 0 ? '100%' : '-100%',
        opacity: 1,
      };
    },
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction > 0 ? '-100%' : '100%',
      opacity: 1,
    }),
  };

  const transition = {
    type: 'spring' as const,
    stiffness: 300,
    damping: 30,
    mass: 0.8,
  };

  return (
    <div className="w-full min-h-screen relative overflow-hidden flex flex-col">
      {/* Header Status Bar - Fixed at top */}
      <HeaderStatusBar
        user={userProfile}
        isFirstMount={isFirstMount.current}
        onShopClick={() => handleTabChange('shop')}
        onModalStateChange={setIsModalOpen}
      />

      {/* Content Area with directional slide animation */}
      <div
        className="flex-1 overflow-hidden relative"
        style={{
          backgroundImage: `url(${bgHome})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <AnimatePresence initial={true} custom={direction}>
          {activeTab === 'battle' && (
            <motion.div
              key="battle"
              custom={direction}
              variants={contentVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
              className="absolute inset-0 overflow-y-auto"
            >
              <BattleContent
                key={battleContentKey}
                userProfile={userProfile}
                onRefreshContent={handleBattleContentRefresh}
              />
            </motion.div>
          )}

          {activeTab === 'quests' && (
            <motion.div
              key="quests"
              custom={direction}
              variants={contentVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
              className="absolute inset-0 overflow-y-auto"
            >
              <QuestsContent />
            </motion.div>
          )}

          {activeTab === 'shop' && (
            <motion.div
              key="shop"
              custom={direction}
              variants={contentVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
              className="absolute inset-0 overflow-y-auto"
            >
              <ShopContent />
            </motion.div>
          )}

          {activeTab === 'frens' && (
            <motion.div
              key="frens"
              custom={direction}
              variants={contentVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
              className="absolute inset-0 overflow-y-auto"
            >
              <FrensContent userProfile={userProfile} />
            </motion.div>
          )}

          {activeTab === 'ranking' && (
            <motion.div
              key="ranking"
              custom={direction}
              variants={contentVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
              className="absolute inset-0 overflow-y-auto"
            >
              <RankingContent userProfile={userProfile} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div
        className="fixed bottom-0 left-0 right-0 max-w-[var(--app-max-width)] mx-auto z-50"
        style={{ visibility: isModalOpen ? 'hidden' : 'visible' }}
      >
        <BottomNavBar
          activeTab={activeTab}
          onTabChange={handleTabChange}
          isFirstMount={isFirstMount.current}
        />
      </div>
    </div>
  );
};
